import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl, FormGroup, FormArray, Validators, AbstractControl } from '@angular/forms';
import {
  CountriesRejectedService,
  CoveragesService,
  RegistercaseBody,
  RegistercaseService
} from '@app/core/services/api';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import moment from 'moment';
import { SpinnerService } from '@app/core/services/spinner/spinner.service';
import { instanceOfError } from '../utils/commonMethods';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-modal-new-case',
  templateUrl: './modal-new-case.component.html',
  styleUrls: ['./modal-new-case.component.scss']
})
export class ModalNewCaseComponent implements OnInit {
  newCaseForm: FormGroup;
  coverages: any[] = [];
  countries: any[];
  minDate: Date;
  maxDate: Date;
  coveragesInCase: any[];
  currenciesMap: string[];
  countryRejected = false;
  private rejectedCountries = new Set<string>();

  constructor(
    public dialogRef: MatDialogRef<ModalNewCaseComponent>,
    private coverageSrv: CoveragesService,
    private countriesRejectedSrv: CountriesRejectedService,
    private createCaseSrv: RegistercaseService,
    private loaderSrv: SpinnerService,
    public translateSrv: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: { cedente?: number; risk?: string }
  ) { }

  ngOnInit() {
    this.coveragesInCase = [];
    const today = new Date();
    this.minDate = new Date(today.getFullYear() - 110, 0, 1);
    this.maxDate = new Date(today.getFullYear(), today.getMonth(), today.getDay());
    this.newCaseForm = new FormGroup({
      referenceNumber: new FormControl('', [Validators.required]),
      birthDate: new FormControl('', [Validators.required]),
      age: new FormControl({ disabled: true, value: '' }, [Validators.required]),
      gender: new FormControl('', [Validators.required]),
      country: new FormControl('', [Validators.required]),
      location: new FormControl('', [Validators.required]),
      duration: new FormControl('', [Validators.required]),
      coverages: new FormArray([])
    });

    this.newCaseForm.controls['country'].valueChanges.subscribe({
      next: (value) => this.onCountryChange(value)
    });

    // Mantener el comportamiento original: forkJoin para cargar coberturas.
    const loader = this.loaderSrv.showSpinner();
    forkJoin([this.coverageSrv.coveragesGet()]).subscribe(
      ([coverageData]) => {
        this.coverages = [...coverageData];
        loader.close();
      },
      () => loader.close()
    );

    this.countriesRejectedSrv
      .countriesRejectedGet()
      .pipe(catchError(() => of([] as string[])))
      .subscribe((rejectedCodes) => {
        this.rejectedCountries = new Set<string>((rejectedCodes ?? []).map((c) => (c ?? '').toString().toUpperCase().trim()).filter((c) => !!c));
        this.onCountryChange(this.newCaseForm?.controls?.['country']?.value);
      });

    this.addCoverage();
    this.newCaseForm.controls['coverages'].valueChanges.subscribe({
      next: (value) => {
        this.coveragesInCase = value.map((item) => ({ key: item.coverage }));
      }
    });
  }

  isRequired(control: AbstractControl) {
    try {
      return control.hasValidator(Validators.required);
    } catch (error) {
      return false;
    }
  }

  isInBlackList(key: string) {
    return this.coveragesInCase.find((item) => item.key === key) !== undefined;
  }

  getCoverageTranslate(key: string) {
    return `RATE.COBERTURAS.${key}`;
  }

  addCoverage() {
    (this.newCaseForm.controls['coverages'] as FormArray).push(
      new FormGroup({
        coverage: new FormControl('', [Validators.required]),
        capital: new FormControl('', [Validators.required]),
        currency: new FormControl('', [Validators.required])
      })
    );
  }

  deleteCoverage(index: number) {
    (this.newCaseForm.controls['coverages'] as FormArray).removeAt(index);
  }

  calcAge(data: MatDatepickerInputEvent<Date>) {
    const birthDate = moment(data.value);
    const today = moment();

    // Calcular edad cronológica (edad real actual)
    let age = today.year() - birthDate.year();

    // Ajustar si aún no ha cumplido años este año
    const birthdayThisYear = moment(birthDate).year(today.year());
    if (today.isBefore(birthdayThisYear, 'day')) {
      age -= 1;
    }

    // Calcular el próximo cumpleaños
    const nextBirthday = moment(birthDate).year(today.year());
    if (nextBirthday.isSameOrBefore(today, 'day')) {
      nextBirthday.add(1, 'year');
    }

    // Calcular meses hasta el próximo cumpleaños
    const monthsUntilBirthday = nextBirthday.diff(today, 'months', true);

    // Si faltan 6 meses o menos, sumar 1 año a la edad cronológica
    if (monthsUntilBirthday <= 6) {
      age += 1;
    }

    this.newCaseForm.get('age').setValue(age);
  }

  close() {
    this.dialogRef.close();
  }

  createCase() {
    if (this.countryRejected) {
      return;
    }
    const loader = this.loaderSrv.showSpinner();

    let body: RegistercaseBody;
    if (this.data.cedente) {
      body = {
        _case: { ...this.newCaseForm.getRawValue() },
        cedente: this.data.cedente
      };
    } else {
      body = { ...this.newCaseForm.getRawValue() };
    }
    // console.log(body);
    this.createCaseSrv.registercasePost(body).subscribe(
      (response: any) => {
        loader.close();
        if (instanceOfError(response)) {
          this.dialogRef.close(response);
        } else {
          this.dialogRef.close(response.newCase);
        }
      },
      (error) => loader.close()
    );
  }

  private onCountryChange(value: any) {
    const code = (value ?? '').toString().toUpperCase().trim();
    const isRejected = !!code && this.rejectedCountries.has(code);
    this.setCountryRejected(isRejected);
  }

  private setCountryRejected(isRejected: boolean) {
    this.countryRejected = isRejected;
    const control = this.newCaseForm?.controls?.['country'];
    if (!control) {
      return;
    }

    const currentErrors = control.errors ?? {};

    if (isRejected) {
      control.setErrors({ ...currentErrors, rejectedCountry: true });
      control.markAsTouched();
      return;
    }

    if (currentErrors.rejectedCountry) {
      const { rejectedCountry, ...rest } = currentErrors as any;
      const nextErrors = Object.keys(rest).length ? rest : null;
      control.setErrors(nextErrors);
    }
  }
}
